import z from "zod";

import { nullableToOptional } from "./utils";

// Wire-shape schema for stored availability windows. Intentionally looser than
// the form-input `availabilityWindowSchema` (no date regex, no
// anyTime/startMinute/endMinute cross-field invariants): a single stored row
// that violates those invariants must not be allowed to break parsing of every
// worker the employer search returns.
export const postedAvailabilityWindowResponseSchema = z.object({
  date: z.string(),
  anyTime: z.boolean(),
  startMinute: nullableToOptional(z.number().int()),
  endMinute: nullableToOptional(z.number().int()),
});

export type PostedAvailabilityWindowResponse = z.infer<
  typeof postedAvailabilityWindowResponseSchema
>;

export const ProfileSchema = z
  .object({
    createdAt: nullableToOptional(z.string()),
    user_id: z.string(),
    distance: nullableToOptional(z.number()),
    full_name: nullableToOptional(z.string()),
    about_me: nullableToOptional(z.string()),
    avatar: nullableToOptional(z.string()),
    avatar_url: nullableToOptional(z.string()),
    preferred_roles: nullableToOptional(z.array(z.string())),
    posted_availability: nullableToOptional(
      z.array(postedAvailabilityWindowResponseSchema),
    ),
    skills: nullableToOptional(z.array(z.string())),
    tax_document: nullableToOptional(z.unknown()),
    rating: nullableToOptional(z.number()),
    // Published overall 1-5 rating; null until rating_count reaches the threshold.
    overall_rating: nullableToOptional(z.number()),
    rating_count: nullableToOptional(z.number().int()),
    total_review: nullableToOptional(z.number()),
    address: nullableToOptional(z.string()),
    zipcode: nullableToOptional(z.string()),
    state: nullableToOptional(z.string()),
    latitude: nullableToOptional(z.number()),
    longitude: nullableToOptional(z.number()),
    // Commute radius in miles; employer worker-search filters on it.
    available_distance: nullableToOptional(z.number()),
    resume_url: nullableToOptional(z.string()),
    resume_signed_url: nullableToOptional(z.string()),
    preferred_language: nullableToOptional(z.enum(["en", "es"])),
    // Roster-aware fields the employer worker-search attaches so Search can
    // mirror the roster "Worked for you before" column (absent elsewhere).
    worked_before: nullableToOptional(z.boolean()),
    prior_shift_count: nullableToOptional(z.number().int()),
    last_worked_at: nullableToOptional(z.string()),
    starred: nullableToOptional(z.boolean()),
    next_shift: nullableToOptional(
      z.object({
        id: z.number().nullable().optional(),
        start_at: z.string().nullable().optional(),
      }),
    ),
  })
  .passthrough();

export type Profile = z.infer<typeof ProfileSchema>;

export const ProfileResponseSchema = z.object({
  profile: ProfileSchema,
  message: z.string().nullable(),
  success: z.boolean(),
  shifts_completed: nullableToOptional(z.number().int()),
  experience_roles: nullableToOptional(z.array(z.string())),
});

export type ProfileResponse = z.infer<typeof ProfileResponseSchema>;
